import React, {
  useState, useCallback, useEffect, useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table } from 'antd';
import PropTypes from 'prop-types';

import BorderlessButton from '../../common/buttons/BorderlessButton';
import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import CompanySettingsCard from '../CompanySettingsCard';
import SettingsCardHeader from '../SettingsCardHeader';

import colors from '../../constants/Colors';

import BoardCardDrawer from './BoardCardDrawer';
import BoardCardDeleteModal from './BoardCardDeleteModal';

import { getCustomTables, getStatuses } from '../../forms/state/forms.actions';
import { getCustomFields } from '../../timecards/state/timecards.actions';
import { getProjectCustomFieldTemplate } from '../../projects/state/projects.actions';
import { getCustomerCustomFieldTemplate } from '../../contacts/customers/state/customers.actions';

const onHeaderCell = () => ({
  style: {
    backgroundColor: 'white',
  },
});

function Boards({
  visible = true,
}) {
  const dispatch = useDispatch();
  const boardCardTemplates = useSelector((state) => state.boards.cardTemplates);

  const [addDrawerOpen, setAddDrawerOpen] = useState(false);
  const [cardToDelete, setCardToDelete] = useState({});
  const [editData, setEditData] = useState({});

  const closeAddDrawer = useCallback(() => {
    setAddDrawerOpen(false);
    setEditData({});
  }, []);
  const openAddDrawer = useCallback(() => setAddDrawerOpen(true), []);
  const closeDeleteModal = useCallback(() => setCardToDelete({}), []);

  const boardCards = useMemo(() => (
    Object.values(boardCardTemplates)
  ), [boardCardTemplates]);

  useEffect(() => {
    if (!visible) setEditData({});
  }, [visible]);

  useEffect(() => {
    dispatch(getCustomTables());
    dispatch(getCustomFields());
    dispatch(getStatuses());
    dispatch(getProjectCustomFieldTemplate());
    dispatch(getCustomerCustomFieldTemplate());
  }, []);

  const columns = useMemo(() => [{
    title: 'Name',
    dataIndex: 'title',
    onHeaderCell,
  },
  {
    dataIndex: 'edit',
    align: 'edit',
    onHeaderCell,
    width: 60,
    render: (_, record) => {
      const isEdit = record.labelId in editData;
      const icon = isEdit ? 'close' : 'edit';
      return (
        <BorderlessButton
          title=""
          icon={icon}
          color={colors.ONTRACCR_BLACK}
          onClick={() => {
            setEditData(record);
            setAddDrawerOpen(true);
          }}
          style={{
            paddingRight: 8,
            paddingLeft: 0,
          }}
        />
      );
    },
  },
  {
    dataIndex: 'action',
    key: 'action',
    align: 'center',
    width: 60,
    onHeaderCell,
    render: (_, record) => (
      <BorderlessButton
        title=""
        icon="delete"
        color="red"
        onClick={() => setCardToDelete(record)}
        style={{
          paddingRight: 8,
          paddingLeft: 0,
        }}
      />
    ),
  }], []);

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <SettingsCardHeader title="Board Card Types">
        <OnTraccrButton
          title="Add"
          type="primary"
          onClick={openAddDrawer}
        />
      </SettingsCardHeader>
      <CompanySettingsCard
        style={{
          marginTop: 20,
          padding: 0,
          height: 'auto',
        }}
      >
        <Table
          columns={columns}
          dataSource={boardCards}
          pagination={false}
          size="small"
          scroll={{ y: 'calc(100vh - 300px)', x: 'hidden' }}
        />
      </CompanySettingsCard>
      <BoardCardDrawer
        visible={addDrawerOpen}
        onClose={closeAddDrawer}
        editData={editData}
      />
      <BoardCardDeleteModal
        visible={!!cardToDelete.id}
        id={cardToDelete.id}
        title={cardToDelete.title}
        onClose={closeDeleteModal}
      />
    </div>
  );
}

Boards.propTypes = {
  visible: PropTypes.bool,
};

Boards.defaultProps = {
  visible: true,
};

export default Boards;
